/* eslint-disable implicit-arrow-linebreak */
import { setElementVars } from '@vanilla-extract/dynamic';
import { clone, mergeDeepRight } from 'ramda';
import tinycolor from 'tinycolor2';
import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';

import { EVO_THEME } from '@ezugi/bootstrap/src/variants/components/evo/evo-theme.css';
import { DeepPartial } from '@ezugi/types';
import { isEvoWrapped } from '@ezugi/utils/src/functions/skins';

import { Theme, defaultTheme, theme as vanillaExtractTheme } from './theme.css';
import { getRgbRaw, mergeKeepShape } from './utils';

declare const window: {
  THEME_DERIVED_VALUES: Record<string, ThemeDerivedEntry>;
} & Window;

export type Skin = DeepPartial<Theme>

type ColorMode = 'brighten' | 'darken' | 'lighten';

type ColorKey = keyof Theme['palette']

type ThemeDerivedEntry = {
  key: ColorKey;
  mode: ColorMode;
  amount: number;
}

const themeStore = createStore(
  devtools(() => ({
    theme: defaultTheme,
  }), { store: 'theme-store', name: 'Theme Store', enabled: false })
);

export const setTheme = (skin: Skin = {}) => {
  // Merge skin into theme. skin.palette colors will be used, with fallback on theme.palette
  try {
    const prevTheme = themeStore.getState().theme;
    const newTheme = mergeKeepShape(prevTheme, skin);

    if (isEvoWrapped()) {
      newTheme.sizes = EVO_THEME.sizes;
    }

    vanillaExtractSetTheme(newTheme);
    setLegacyTheme(newTheme);

    themeStore.setState({
      theme: newTheme,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to set theme', error,);
  }
};

const vanillaExtractSetTheme = (newTheme: Theme) => {
  const rawPalette: Partial<Theme['palette']> = {};
  const validPalette: Partial<Theme['palette']> = {};

  Object.keys(newTheme.palette).forEach((prop) => {
    const themeKey = prop as keyof typeof newTheme.palette;

    const parsed = tinycolor(newTheme.palette[themeKey]);

    const rgbString = parsed.isValid() ? getRgbRaw(parsed.toRgb())
      : getRgbRaw(tinycolor(defaultTheme.palette[themeKey]).toRgb());

    const validColor = parsed.isValid()
      ? newTheme.palette[themeKey]
      : defaultTheme.palette[themeKey];

    rawPalette[themeKey] = rgbString;
    validPalette[themeKey] = validColor;
  });

  const finalTheme = mergeDeepRight(
    newTheme,
    { palette: validPalette,
      paletteRaw: rawPalette }
  );

  const el = document.documentElement;
  setElementVars(el, vanillaExtractTheme, clone(finalTheme));
};

const setLegacyTheme = (newTheme: Theme) => {
  // Add theme palette entries as CSS variables
  Object.keys(newTheme.palette).forEach((prop) => {
    const themeKey = prop as keyof typeof newTheme.palette;

    const parsed = tinycolor(newTheme.palette[themeKey]);
    const rgbString = parsed.isValid() ? getRgbRaw(parsed.toRgb())
      : getRgbRaw(tinycolor(defaultTheme.palette[themeKey]).toRgb());

    document.documentElement.style.setProperty(`--theme-${themeKey}-color`, rgbString);
  });

  // set derived colors
  const derivedColors = Object.keys(window.THEME_DERIVED_VALUES ?? {});
  derivedColors.forEach((derivedKey) => {
    const { key, mode, amount } = window.THEME_DERIVED_VALUES[derivedKey];
    const themeColorModeKey = `--theme-${key}-color-${mode}-${amount}`;
    const themeColorKey = `--theme-${key}-color`;
    const getColorValue = getComputedStyle(document.documentElement);
    const color = getColorValue.getPropertyValue(themeColorKey);
    const rgb = tinycolor(`rgb(${color})`)[mode](amount).toRgb();
    const rgbString = `${rgb.r}, ${rgb.g}, ${rgb.b}`;
    document.documentElement.style.setProperty(themeColorModeKey, rgbString);
  });
};
